import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Brain, PawPrint, Trophy, Sparkles, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useProgress } from '@/hooks/useProgress';
import { species, getRandomFact, journeys } from '@/data';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function Home() {
  const { progress } = useProgress();
  const randomFact = getRandomFact();

  const globalJourney = journeys.find(j => j.id === 'global-expert');
  const currentLevel = globalJourney?.levels.findLast(l => l.xpRequired <= progress.globalStats.totalXp) || globalJourney?.levels[0];
  const nextLevel = globalJourney?.levels.find(l => l.xpRequired > progress.globalStats.totalXp);

  const xpProgress = nextLevel
    ? ((progress.globalStats.totalXp - (currentLevel?.xpRequired || 0)) / ((nextLevel.xpRequired) - (currentLevel?.xpRequired || 0))) * 100
    : 100;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-lg mx-auto px-4 py-6 space-y-6"
    >
      {/* Welcome Section */}
      <motion.div variants={itemVariants} className="text-center">
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="text-6xl mb-4"
        >
          🦦
        </motion.div>
        <h1 className="text-3xl font-display font-bold text-slate-800 dark:text-white mb-2">
          Welcome to Otter Expert!
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Discover the amazing world of otters
        </p>
      </motion.div>

      {/* Progress Card */}
      <motion.div variants={itemVariants}>
        <Card className="bg-gradient-to-br from-otter-50 to-water-50 dark:from-otter-900/50 dark:to-water-900/50 border-otter-200 dark:border-otter-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Your Level</p>
              <p className="text-2xl font-bold text-otter-700 dark:text-otter-300">
                {currentLevel?.name || 'Curious Pup'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total XP</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {progress.globalStats.totalXp.toLocaleString()}
              </p>
            </div>
          </div>

          {nextLevel && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                <span>Progress to {nextLevel.name}</span>
                <span>{Math.round(xpProgress)}%</span>
              </div>
              <ProgressBar value={xpProgress} color="xp" size="md" />
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-otter-200 dark:border-otter-700">
            <div className="text-center">
              <p className="text-2xl font-bold text-otter-600 dark:text-otter-400">
                {progress.speciesViewed.length}
              </p>
              <p className="text-xs text-slate-500">Species Seen</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {progress.globalStats.totalCorrect}
              </p>
              <p className="text-xs text-slate-500">Correct Answers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {progress.streaks.current}
              </p>
              <p className="text-xs text-slate-500">Day Streak</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={itemVariants}>
        <h2 className="text-lg font-display font-bold text-slate-800 dark:text-white mb-3">
          Start Learning
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/quiz">
            <Card hoverable className="h-full">
              <div className="flex flex-col items-center text-center py-2">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-2">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white">Quiz</h3>
                <p className="text-xs text-slate-500">Test your knowledge</p>
              </div>
            </Card>
          </Link>

          <Link to="/learn">
            <Card hoverable className="h-full">
              <div className="flex flex-col items-center text-center py-2">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mb-2">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white">Learn</h3>
                <p className="text-xs text-slate-500">Explore facts</p>
              </div>
            </Card>
          </Link>

          <Link to="/species">
            <Card hoverable className="h-full">
              <div className="flex flex-col items-center text-center py-2">
                <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center mb-2">
                  <PawPrint className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white">Species</h3>
                <p className="text-xs text-slate-500">All 13 otters</p>
              </div>
            </Card>
          </Link>

          <Link to="/achievements">
            <Card hoverable className="h-full">
              <div className="flex flex-col items-center text-center py-2">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mb-2">
                  <Trophy className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white">Trophies</h3>
                <p className="text-xs text-slate-500">Your achievements</p>
              </div>
            </Card>
          </Link>
        </div>
      </motion.div>

      {/* Daily Fact */}
      <motion.div variants={itemVariants}>
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-200 dark:border-amber-700">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-white mb-1">
                Did you know?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {randomFact.content}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Species Preview */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display font-bold text-slate-800 dark:text-white">
            Otter Species
          </h2>
          <Link to="/species" className="text-sm text-otter-600 dark:text-otter-400 font-medium">
            See all →
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
          {species.slice(0, 5).map((otter) => (
            <Link
              key={otter.id}
              to={`/species/${otter.id}`}
              className="flex-shrink-0 w-32"
            >
              <Card hoverable padded={false} className="overflow-hidden">
                <div className="aspect-square bg-slate-200 dark:bg-slate-700 relative">
                  <div className="absolute inset-0 flex items-center justify-center text-4xl">
                    🦦
                  </div>
                </div>
                <div className="p-2">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                    {otter.commonName}
                  </h3>
                  <p className="text-xs text-slate-500 italic truncate">
                    {otter.scientificName}
                  </p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
